import { TCreateModifierVisibleForm, TTypeOfCreateModifierModalWindow } from './createModifierContext';
import {
	TFileId,
	ICreateModifierValidationError,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	IServerError,
	TLanguageIDs,
	IMenuTranslation,
	IDefaultLanguage,
	ICreateModifier,
	ITotalPrice,
} from 'types';

export enum CreateModifierActionTypes {
	CHANGE_VISIBLE_FORM = 'CHANGE_VISIBLE_FORM',
	SET_FILE_ID = 'SET_FILE_ID',
	SET_FETCHED_CUSTOMERS = 'SET_FETCHED_CUSTOMERS',
	SET_CUSTOMER = 'SET_CUSTOMER',
	SET_FETCHED_MODIFIER_GROUPS = 'SET_FETCHED_MODIFIER_GROUPS',
	SET_MODIFIER_GROUPS = 'SET_MODIFIER_GROUPS',
	CHANGE_NAME = 'CHANGE_NAME',
	CHANGE_DESCRIPTION = 'CHANGE_DESCRIPTION',
	SET_FETCHED_RESTAURANTS = 'SET_FETCHED_RESTAURANTS',
	SET_RESTAURANTS_FOR_PRICE = 'SET_RESTAURANTS_FOR_PRICE',
	CHANGE_PRICE = 'CHANGE_PRICE',
	ADD_PRICE = 'ADD_PRICE',
	REMOVE_PRICE = 'REMOVE_PRICE',
	SET_IS_DISABLED_IN_RESTAURANTS = 'SET_IS_DISABLED_IN_RESTAURANTS',
	SET_IS_RESTRICTED_IN_RESTAURANTS = 'SET_IS_RESTRICTED_IN_RESTAURANTS',
	SET_IS_PRESELECTED_IN_RESTAURANTS = 'SET_IS_PRESELECTED_IN_RESTAURANTS',
	SET_QUANTITY_REGULATION_RESTAURANTS = 'SET_QUANTITY_REGULATION_RESTAURANTS',
	CHANGE_QUANTITY_MAX_LIMIT = 'CHANGE_QUANTITY_MAX_LIMIT',
	SET_FETCHED_TRANSLATIONS = 'SET_FETCHED_TRANSLATIONS',
	SET_TRANSLATION_TAB = 'SET_TRANSLATION_TAB',
	CHANGE_TRANSLATION = 'CHANGE_TRANSLATION',
	CHANGE_ACTIVATION_SCHEDULE = 'CHANGE_ACTIVATION_SCHEDULE',
	CHANGE_DEACTIVATION_SCHEDULE = 'CHANGE_DEACTIVATION_SCHEDULE',
	SET_DATA_FOR_SUBMIT = 'SET_DATA_FOR_SUBMIT',
	CHANGE_TYPE_OF_MODAL_WINDOW = 'CHANGE_TYPE_OF_MODAL_WINDOW',
	SHOW_ERROR_MODAL_WITH_INFO = 'SHOW_ERROR_MODAL_WITH_INFO',
	RESET_FIELDS = 'RESET_FIELDS',
}

interface IChangeVisibleForm {
	type: CreateModifierActionTypes.CHANGE_VISIBLE_FORM;
	payload: {
		visibleForm: TCreateModifierVisibleForm;
	};
}

interface ISetFileId {
	type: CreateModifierActionTypes.SET_FILE_ID;
	payload: {
		fileId: TFileId;
	};
}

interface ISetFetchedCustomers {
	type: CreateModifierActionTypes.SET_FETCHED_CUSTOMERS;
	payload: {
		customers: IItemForGetShortInfo[];
	};
}

interface ISetCustomer {
	type: CreateModifierActionTypes.SET_CUSTOMER;
	payload: {
		customer: Nullable<string>;
	};
}

interface ISetFetchedRestaurants {
	type: CreateModifierActionTypes.SET_FETCHED_RESTAURANTS;
	payload: {
		restaurants: IItemForGetShortInfoWithStatus[];
	};
}

interface ISetRestaurantsForPrice {
	type: CreateModifierActionTypes.SET_RESTAURANTS_FOR_PRICE;
	payload: {
		restaurants: string[];
	};
}

interface IChangePrice {
	type: CreateModifierActionTypes.CHANGE_PRICE;
	payload: {
		price: string;
	};
}

interface IAddPrice {
	type: CreateModifierActionTypes.ADD_PRICE;
	payload: {
		prices: ITotalPrice[];
	};
}

interface IRemovePrice {
	type: CreateModifierActionTypes.REMOVE_PRICE;
	payload: {
		updatedTotalPrices: ITotalPrice[];
		updatedRestaurantsForPrices: string[];
		updatedIsDisabledInRestaurants: string[];
		updatedIsRestrictedInRestaurants: string[];
		updatedIsPreselectedInRestaurants: string[];
		updatedQuantityRegulationRestaurants: string[];
	};
}

interface ISetFetchedModifierGroups {
	type: CreateModifierActionTypes.SET_FETCHED_MODIFIER_GROUPS;
	payload: {
		modifierGroups: IItemForGetShortInfo[];
	};
}

interface ISetModifierGroups {
	type: CreateModifierActionTypes.SET_MODIFIER_GROUPS;
	payload: {
		modifierGroups: string[];
	};
}

interface IChangeName {
	type: CreateModifierActionTypes.CHANGE_NAME;
	payload: {
		name: string;
	};
}

interface IChangeDescription {
	type: CreateModifierActionTypes.CHANGE_DESCRIPTION;
	payload: {
		description: string;
	};
}

interface ISetIsDisabledInRestaurants {
	type: CreateModifierActionTypes.SET_IS_DISABLED_IN_RESTAURANTS;
	payload: {
		disabledInRestaurants: string[];
	};
}

interface ISetIsRestrictedInRestaurants {
	type: CreateModifierActionTypes.SET_IS_RESTRICTED_IN_RESTAURANTS;
	payload: {
		restrictedInRestaurants: string[];
	};
}

interface ISetIsPreselectedInRestaurants {
	type: CreateModifierActionTypes.SET_IS_PRESELECTED_IN_RESTAURANTS;
	payload: {
		preselectedInRestaurants: string[];
	};
}

interface ISetQuantityRegulationRestaurants {
	type: CreateModifierActionTypes.SET_QUANTITY_REGULATION_RESTAURANTS;
	payload: {
		quantityRegulationRestaurants: string[];
	};
}

interface IChangeQuantityMaxLimit {
	type: CreateModifierActionTypes.CHANGE_QUANTITY_MAX_LIMIT;
	payload: {
		quantityMaxLimit: number;
	};
}

interface ISetFetchedTranslation {
	type: CreateModifierActionTypes.SET_FETCHED_TRANSLATIONS;
	payload: {
		translations: IMenuTranslation[];
		defaultCustomerLanguage: Nullable<IDefaultLanguage>;
	};
}

interface ISetTranslationTab {
	type: CreateModifierActionTypes.SET_TRANSLATION_TAB;
	payload: {
		selectedTranslationTab: TLanguageIDs | null;
	};
}

interface IChangeTranslation {
	type: CreateModifierActionTypes.CHANGE_TRANSLATION;
	payload: {
		translations: IMenuTranslation[];
	};
}

interface IChangeActivationSchedule {
	type: CreateModifierActionTypes.CHANGE_ACTIVATION_SCHEDULE;
	payload: {
		activationSchedule: string;
	};
}

interface IChangeDeactivationSchedule {
	type: CreateModifierActionTypes.CHANGE_DEACTIVATION_SCHEDULE;
	payload: {
		deactivationSchedule: string;
	};
}

interface ISetDataForSubmit {
	type: CreateModifierActionTypes.SET_DATA_FOR_SUBMIT;
	payload: {
		dataForSubmit: ICreateModifier | null;
	};
}

interface IChangeTypeOfModalWindow {
	type: CreateModifierActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW;
	payload: {
		typeOfModalWindow: TTypeOfCreateModifierModalWindow;
	};
}

interface IShowErrorModalWithInfo {
	type: CreateModifierActionTypes.SHOW_ERROR_MODAL_WITH_INFO;
	payload: {
		errorResponseData: IServerError<ICreateModifierValidationError> | null;
	};
}

interface IResetSelectedControls {
	type: CreateModifierActionTypes.RESET_FIELDS;
}

export type TCreateModifierAction =
	| ISetFileId
	| IChangeVisibleForm
	| ISetFetchedCustomers
	| ISetCustomer
	| ISetFetchedModifierGroups
	| ISetModifierGroups
	| IChangeName
	| IChangeDescription
	| ISetFetchedRestaurants
	| ISetRestaurantsForPrice
	| IChangePrice
	| IAddPrice
	| IRemovePrice
	| ISetIsDisabledInRestaurants
	| ISetIsRestrictedInRestaurants
	| ISetIsPreselectedInRestaurants
	| ISetQuantityRegulationRestaurants
	| IChangeQuantityMaxLimit
	| ISetFetchedTranslation
	| ISetTranslationTab
	| IChangeTranslation
	| IChangeActivationSchedule
	| IChangeDeactivationSchedule
	| ISetDataForSubmit
	| IChangeTypeOfModalWindow
	| IShowErrorModalWithInfo
	| IResetSelectedControls;
